import type { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { BehaviorSubject, Observable, catchError, filter, switchMap, take, throwError } from 'rxjs';

import { AuthState, Logout, RefreshToken } from '@data/state';
import { AlertService } from '@core/service/alert/alert.service';

// 401/400 here means bad credentials/token, not an expired session — let the
// component handle it inline instead of triggering a refresh or logout.
const UNAUTHENTICATED_AUTH_ENDPOINTS = ['/auth/login', '/auth/forgot-password', '/auth/reset-password', '/auth/confirm-email'];

// Module-scoped so concurrent 401s coalesce onto one refresh call, not one each.
const REFRESH_FAILED = Symbol('refresh-failed');
type RefreshSignal = string | null | typeof REFRESH_FAILED;
let isRefreshing = false;
const refreshedToken$ = new BehaviorSubject<RefreshSignal>(null);

const addToken = (request: HttpRequest<unknown>, token: string): HttpRequest<unknown> =>
    request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });

export const baseHttpInterceptor: HttpInterceptorFn = (request, next) => {
    const store = inject(Store);
    const router = inject(Router);
    const alertService = inject(AlertService);

    const token = store.selectSnapshot(AuthState.token);
    const authedRequest = token ? addToken(request, token) : request;

    const isUnauthenticatedAuthEndpoint = UNAUTHENTICATED_AUTH_ENDPOINTS.some((path) => request.url.includes(path));
    const isRefreshEndpoint = request.url.includes('/auth/refresh');

    return next(authedRequest).pipe(
        catchError((error: HttpErrorResponse) => {
            if (isUnauthenticatedAuthEndpoint) {
                return throwError(() => error);
            }

            if (isRefreshEndpoint) {
                // Refresh call itself 401'd — session's dead, not retriable.
                if (error?.status === 401) forceLogout(store, router, alertService);
                return throwError(() => error);
            }

            if (error?.status === 401) {
                return handleUnauthorized(authedRequest, next, store, router, alertService);
            }

            switch (error?.status) {
                case 400:
                    alertService.error(error.error?.data?.message ?? 'Bad request');
                    break;
                case 403:
                    alertService.error('You do not have permission to perform this action.');
                    break;
                case 404:
                    alertService.error(error.error?.message ?? 'Resource not found.');
                    break;
                case 500:
                    alertService.error(error.error?.message ?? 'An unexpected error occurred.');
                    break;
                default:
                    alertService.error('Something went wrong. Please try again.');
                    break;
            }
            return throwError(() => error);
        }),
    );
};

function handleUnauthorized(
    request: HttpRequest<unknown>,
    next: (req: HttpRequest<unknown>) => Observable<HttpEvent<unknown>>,
    store: Store,
    router: Router,
    alertService: AlertService,
): Observable<HttpEvent<unknown>> {
    const refreshToken = store.selectSnapshot(AuthState.refreshToken);
    if (!refreshToken) {
        forceLogout(store, router, alertService);
        return throwError(() => new Error('Session expired.'));
    }

    if (!isRefreshing) {
        isRefreshing = true;
        refreshedToken$.next(null);

        return store.dispatch(new RefreshToken()).pipe(
            switchMap(() => {
                isRefreshing = false;
                const newToken = store.selectSnapshot(AuthState.token);
                refreshedToken$.next(newToken);
                return next(addToken(request, newToken!));
            }),
            catchError((error) => {
                isRefreshing = false;
                refreshedToken$.next(REFRESH_FAILED);
                forceLogout(store, router, alertService);
                return throwError(() => error);
            }),
        );
    }

    // A refresh is already in flight — wait for it, then retry with its token.
    // On failure, unblock waiters with an error too, or they'd hang forever.
    return refreshedToken$.pipe(
        filter((signal): signal is string | typeof REFRESH_FAILED => signal !== null),
        take(1),
        switchMap((signal) =>
            signal === REFRESH_FAILED ? throwError(() => new Error('Session expired.')) : next(addToken(request, signal)),
        ),
    );
}

function forceLogout(store: Store, router: Router, alertService: AlertService): void {
    store.dispatch(new Logout());
    router.navigate(['/auth/login']);
    alertService.warning('Session expired. Please log in again.');
}
